import { Card, Form, Row, Col, Button, Spinner, Alert } from "react-bootstrap";
import { Product } from "../../../store/cart-store";
import OrderSummary from "./OrderSummary";
import { useSaveOrder, CheckoutData } from "../hooks/use-save-order";

interface PaymentMethodSelectorProps {
  onPaymentMethodSelect: (method: string) => void;
  selectedPaymentMethod?: string | null;
  checkoutData: CheckoutData | null;
  cartItems: Product[];
  onOrderSuccess: (response: any) => void;
  onOrderError: (error: Error) => void;
}

// Orden de presentacion de los metodos de pago; los no listados quedan al final.
const METHOD_ORDER = [
  "nequi_payment", // Bre-B
  "pse_payment",
  "cash_efecty",
  "card_payment",
  "wompi",
];

const methodRank = (id: string) => {
  const index = METHOD_ORDER.indexOf(id);
  return index === -1 ? METHOD_ORDER.length : index;
};

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  checkoutData,
  cartItems,
  onPaymentMethodSelect,
  onOrderSuccess,
  onOrderError,
}) => {
  const {
    isProcessing,
    isLoading,
    isError,
    error,
    selectedPaymentMethod,
    paymentMethods,
    isLoadingPaymentMethods,
    isErrorPaymentMethods,
    handlePaymentMethodChange,
    setSelectedPaymentMethod,
    handleProcessOrder,
    paymentGateways,
  } = useSaveOrder(onPaymentMethodSelect);

  const processOrder = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    handleProcessOrder(
      { ...checkoutData! },
      cartItems,
      onOrderSuccess,
      onOrderError,
    );
  };

  const storePickupExtraMethods = [];

  if (paymentGateways.includes("monnet")) {
    storePickupExtraMethods.push(
      {
        id: "pse_payment",
        name: "Pago con PSE",
        description: "Realiza pagos en línea a través del sistema PSE.",
        logo: "/images/payment/logo-pse.jpg",
      },
      {
        id: "card_payment",
        name: "Pago con tarjeta de crédito o débito",
        description:
          "Paga con tus tarjetas de crédito o débito de forma segura.",
        logo: "/images/payment/tarjeta-logo.jpg",
      },
      // {
      //   id: "bank_transfer",
      //   name: "Pago con corresponsal Bancolombia",
      //   description: "Realiza una transferencia directa al punto de venta.",
      //   logo: "/images/payment/bancolombia-logo.jpg",
      // },
      {
        id: "cash_efecty",
        name: "Pago con Efecty",
        description: "Realiza pagos en efectivo a través de Efecty.",
        logo: "/images/payment/efecty-logo.jpg",
      },
      {
        id: "nequi_payment",
        name: "Pago con Bre-B",
        description:
          "Paga fácilmente usando tu Llave BRE-B desde cualquier banco compatible, como Nequi, Bancolombia o Nu.",
        logo: "/images/payment/breblogo.png",
      },
    );
  }

  if (checkoutData?.deliveryType === "STORE_PICKUP") {
    storePickupExtraMethods.push({
      id: "cash_payment",
      name: "Pago en efectivo en tienda",
      description: "Paga directamente al recoger tu pedido en la tienda.",
      logo: "",
    });
  }

  const displayedPaymentMethods = [
    ...(paymentMethods || []),
    ...storePickupExtraMethods,
  ].sort((a, b) => methodRank(a.id) - methodRank(b.id));

  return (
    <div className="payment-method-selector">
      <h3 className="mb-4">Selecciona tu método de pago</h3>

      {isLoadingPaymentMethods && (
        <div className="text-center mb-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando métodos de pago...</span>
          </Spinner>
          <p className="mt-2">Cargando métodos de pago disponibles...</p>
        </div>
      )}

      {isErrorPaymentMethods && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>
            No se pudieron cargar los métodos de pago
          </Alert.Heading>
          <p>
            Ocurrió un error al cargar los métodos de pago disponibles. Por
            favor, intenta nuevamente más tarde.
          </p>
        </Alert>
      )}

      {!isLoadingPaymentMethods &&
        displayedPaymentMethods &&
        displayedPaymentMethods.length === 0 && (
          <Alert variant="info" className="mb-4">
            <Alert.Heading>No hay métodos de pago disponibles</Alert.Heading>
            <p>
              No se encontraron métodos de pago disponibles para tu ubicación.
              Por favor, contacta a soporte.
            </p>
          </Alert>
        )}

      <Row>
        <Col lg={8}>
          <Form>
            {displayedPaymentMethods &&
              displayedPaymentMethods.length > 0 &&
              displayedPaymentMethods.map((method) => (
                <Card
                  key={method.id}
                  className={`mb-3 payment-method-card ${
                    selectedPaymentMethod === method.id
                      ? "border-primary"
                      : "border"
                  }`}
                  onClick={() => {
                    setSelectedPaymentMethod(method.id);
                    if (onPaymentMethodSelect) {
                      onPaymentMethodSelect(method.id);
                    }
                  }}
                >
                  <Card.Body>
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="radio"
                        id={`payment-${method.id}`}
                        name="paymentMethod"
                        value={method.id}
                        checked={selectedPaymentMethod === method.id}
                        onChange={handlePaymentMethodChange}
                        className="me-3"
                      />
                      {method.logo && (
                        <div
                          className="payment-logo me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: "120px", height: "56px" }}
                        >
                          <img
                            src={method.logo}
                            alt={method.name}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "/images/placeholder.png";
                            }}
                          />
                        </div>
                      )}
                      <div>
                        <h5 className="mb-1">{method.name}</h5>
                        <p className="text-muted mb-0 small">
                          {method.description}
                        </p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
          </Form>
        </Col>

        <Col lg={4}>
          <OrderSummary
            selectedPaymentMethod={selectedPaymentMethod}
            shippingCost={checkoutData?.shippingCost || 0}
            deliveryType={checkoutData?.deliveryType}
          />
          <Card className="mt-4">
            <Card.Body>
              <h5 className="mb-3">
                {selectedPaymentMethod
                  ? `Información de ${
                      displayedPaymentMethods?.find(
                        (m) => m.id === selectedPaymentMethod,
                      )?.name || selectedPaymentMethod
                    }`
                  : "Información del pago"}
              </h5>

              <p className="text-muted">
                {selectedPaymentMethod === "coinpayment" && (
                  <>
                    Al continuar, serás redirigido a la plataforma de
                    CoinPayments para completar tu pago con criptomonedas de
                    forma segura.
                  </>
                )}

                {selectedPaymentMethod === "pse_payment" && (
                  <>
                    Serás redirigido a tu entidad bancaria para realizar el pago
                    a través de PSE de forma segura.
                  </>
                )}

                {selectedPaymentMethod === "card_payment" && (
                  <>
                    Podrás pagar usando tu tarjeta de crédito o débito. El pago
                    se procesará de forma segura.
                  </>
                )}

                {selectedPaymentMethod === "bank_transfer" && (
                  <>
                    Deberás realizar una transferencia bancaria al corresponsal
                    Bancolombia. Recibirás las instrucciones al confirmar tu
                    pedido.
                  </>
                )}

                {selectedPaymentMethod === "cash_efecty" && (
                  <>
                    Podrás realizar el pago en efectivo a través de Efecty
                    siguiendo las instrucciones que recibirás al finalizar la
                    compra.
                  </>
                )}

                {selectedPaymentMethod === "cash_payment" && (
                  <>
                    Podrás pagar en efectivo al momento de recoger tu pedido en
                    la tienda. Asegúrate de llevar el monto exacto.
                  </>
                )}
              </p>

              <Button
                variant="primary"
                className="w-100 mt-3"
                onClick={processOrder}
                disabled={isLoading || isProcessing}
              >
                {isLoading || isProcessing ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Procesando...
                  </>
                ) : (
                  "Finalizar Compra"
                )}
              </Button>

              {isError && (
                <div className="text-danger mt-3">
                  <i className="ri-error-warning-line me-1"></i>
                  {error?.message || "Error al procesar la orden"}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PaymentMethodSelector;
